import {
    Button,
    CircularProgress,
    Grid,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    withTheme
} from '@material-ui/core';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import moment from '../../moment-hawaii';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import AdminHeader from '../atom/AdminHeader';
import ActionIconButton from '../atom/ActionIconButton';
import { EyeIcon } from '../../icons';

/**
 * Manifest monthly timeslots page - shows timeslots in a table with month navigation
 */
function ActivityTimeslotsPage() {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const params = new URLSearchParams(location.search);
    const monthParam = params.get('month');
    const monthStart = monthParam
        ? moment(monthParam, 'YYYY-MM').startOf('month')
        : moment().startOf('month');

    const setMonthStart = updater => {
        const next = updater(monthStart);
        history.replace(`?month=${next.format('YYYY-MM')}`);
    };

    const monthEnd = monthStart.clone().endOf('month');

    const [data, dataLoading] = useSwaggerClient(
        'activities',
        'getManifestTimeslots',
        {
            activityId: id,
            fromDate: monthStart.format('YYYY-MM-DD'),
            toDate: monthEnd.format('YYYY-MM-DD')
        },
        [],
        result => ({
            activityName: result.activityName,
            timeslots: result.timeslots
        })
    );

    const timeslots = (data?.timeslots || [])
        .slice()
        .sort((a, b) => a.activity_start.localeCompare(b.activity_start))
        .filter(slot => slot.paxCount > 0);

    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader title={data?.activityName || 'Manifest'}>
                <Grid item>
                    <Button
                        onClick={() => setMonthStart(s => s.clone().subtract(1, 'month'))}
                        style={{ color: window.clientData?.primaryColor }}
                    >
                        &lt;
                    </Button>
                    <span style={{ margin: '0 8px', fontWeight: 'bold' }}>
                        {monthStart.format('MMMM YYYY')}
                    </span>
                    <Button
                        onClick={() => setMonthStart(s => s.clone().add(1, 'month'))}
                        style={{ color: window.clientData?.primaryColor }}
                    >
                        &gt;
                    </Button>
                </Grid>
            </AdminHeader>
            <Grid item container xs={12} justifyContent="center">
                {dataLoading && timeslots.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <Table style={{ backgroundColor: 'white' }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>PAX</TableCell>
                                <TableCell>Remaining</TableCell>
                                <TableCell>Groups</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {timeslots.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        style={{ padding: '16px' }}
                                    >
                                        No timeslots found for this month.
                                    </TableCell>
                                </TableRow>
                            )}
                            {timeslots.map(slot => {
                                const m = moment(slot.activity_start);
                                return (
                                    <TableRow key={slot.activity_start} hover>
                                        <TableCell>
                                            {m.format('ddd, MMM DD [at] hh:mm A')}
                                        </TableCell>
                                        <TableCell>{slot.paxCount}</TableCell>
                                        <TableCell>
                                            {slot.remaining !== null ? slot.remaining : '—'}
                                        </TableCell>
                                        <TableCell>{slot.groupCount}</TableCell>
                                        <TableCell align="right">
                                            {slot.paxCount > 0 && (
                                                <ActionIconButton
                                                    title="Details"
                                                    icon={EyeIcon}
                                                    to={`/admin/manifest/${id}/${encodeURIComponent(
                                                        slot.activity_start
                                                    )}`}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Grid>
        </Grid>
    );
}

export default withTheme(ActivityTimeslotsPage);
